import axios from 'axios';
import { environment } from '../env/environment';

const API_BASE_URL = environment.baseUrl + '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Send token as Authorization header (localStorage) + cookie fallback
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('agencyToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only handle 401 for actual auth failures, not network errors
    if (error.response?.status === 401) {
      const message = error.response?.data?.message || '';

      // Only logout on actual token/session issues
      if (message.includes('expired') ||
          message.includes('invalid') ||
          message.includes('Authentication required')) {
        localStorage.removeItem('agencyToken');
        // Only redirect if not already on login/register pages
        const currentPath = window.location.pathname;
        if (currentPath !== '/login' && currentPath !== '/register') {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Agency API functions
export const agencyApi = {
  // Profile
  getProfile: () => api.get('/agency/profile'),
  updateProfile: (data) => api.put('/agency/profile', data),

  // Subaccounts
  getSubaccounts: () => api.get('/agency/subaccounts'),
  getSubaccount: (id) => api.get(`/agency/subaccounts/${id}`),
  createSubaccount: (data) => api.post('/agency/subaccounts', data),
  updateSubaccount: (id, data) => api.put(`/agency/subaccounts/${id}`, data),
  deleteSubaccount: (id) => api.delete(`/agency/subaccounts/${id}`),

  // Branding Upload
  uploadBranding: (id, formData) => {
    return api.post(`/agency/upload/${id}/branding`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  // Users
  getUsers: () => api.get('/agency/users'),
  addUser: (data) => api.post('/agency/users', data),
  updateUser: (id, data) => api.put(`/agency/users/${id}`, data),
  deleteUser: (id) => api.delete(`/agency/users/${id}`),

  // User Access Control
  getUserAccess: (id) => api.get(`/agency/users/${id}/access`),
  updateUserAccess: (id, data) => api.put(`/agency/users/${id}/access`, data),
  checkSubaccountAccess: (id) => api.get(`/agency/subaccounts/${id}/check-access`),

  // DNS Instructions
  getDnsInstructions: (id) => api.get(`/tenants/${id}/dns-instructions`),

  // Domain Verification
  verifyDomain: (id, domainType, domain) => api.post(`/agency/subaccounts/${id}/verify-domain`, { domainType, domain }),
  getDomainStatus: (id) => api.get(`/agency/subaccounts/${id}/domain-status`),
  getDnsInstructionsForDomain: (id, domainType) => api.get(`/agency/subaccounts/${id}/dns-instructions/${domainType}`),
  makeDomainPrimary: (id, domainType) => api.post(`/agency/subaccounts/${id}/make-primary`, { domainType }),
  checkDomainPrimary: (id, domainType) => api.get(`/agency/subaccounts/${id}/is-primary/${domainType}`),

  // Sevalla default domains
  getSevallaDefaultDomains: () => api.get('/agency/sevalla-domains'),

  // Webhooks
  getWebhooks: (id) => api.get(`/webhooks/tenant/${id}`),
  updateWebhook: (id, step, url) => api.post(`/webhooks/tenant/${id}/update`, { step, url }),

  // Google Drive (per subaccount)
  getGoogleDriveConfig: (subaccountId) => api.get(`/agency/google-drive/subaccount/${subaccountId}/config`),
  updateGoogleDriveConfig: (subaccountId, formData) => api.post(`/agency/google-drive/subaccount/${subaccountId}/update-config`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteGoogleDriveConfig: (subaccountId) => api.delete(`/agency/google-drive/subaccount/${subaccountId}/config`),
};

export default api;
